import React, { useMemo } from 'react';

import { Typography } from '@mui/material';
import type { LucideIcon } from 'lucide-react';
import { ArrowRightToLine, Forward, ListChecks } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { WorkflowForwardingToggle } from 'src/components';
import {
  PanelHeader,
  PICKER_CHIP_ICON_SIZE,
  ToggleSwitch,
  usePickerChipStyles,
} from 'src/components/generic';
import { ProgressiveColumnIcon } from 'src/components/workflow/board/settings/ColumnItem';
import {
  getColumnPositionInCategory,
  getDoneColumns,
} from 'src/components/workflow/phase-column-helpers';
import { LaunchConfig } from 'src/components/workflow/workflow-builder/LaunchConfig';
import { TemplateCrmConfig } from 'src/components/workflow/workflow-builder/TemplateCrmConfig';
import { ColumnPickerPopover } from 'src/components/workflow/WorkflowBoardRelocationDialog';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { SettingsSection } from './SettingsSection';
import { useWorkflowDetailLinks } from '../WorkflowDetailContext';

const useStyles = makeStyles()(theme => ({
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'auto',
    padding: theme.spacing(2),
  },
  /** Keeps the picker's click target the width of the chip rather than the whole panel. */
  pickerRow: {
    alignItems: 'center',
    display: 'flex',
  },
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
  },
  sectionDescription: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  sectionIcon: {
    color: theme.appColors.iconsType,
    flexShrink: 0,
  },
  sectionLabel: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
  },
  toggleRow: {
    alignItems: 'center',
    display: 'flex',
  },
  toggleText: {
    marginLeft: theme.spacing(2),
  },
}));

interface WorkflowSettingsPanelProps {
  workflowId: UUID;
}

export const WorkflowSettingsPanel = React.memo(function WorkflowSettingsPanel({
  workflowId,
}: WorkflowSettingsPanelProps) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const links = useWorkflowDetailLinks(workflowId);
  const { data } = Apollo.useGetWorkflowWithBoardQuery({ variables: { workflowId } });
  const handleBack = () => navigate(links.baseUrl());
  const isTWF = data?.workflow?.variant === Apollo.WorkflowVariant.Template;

  return (
    <div className={classes.root}>
      <PanelHeader
        showBackButton
        text={isTWF ? 'Template Settings' : 'Workflow Settings'}
        onClickBack={handleBack}
      />
      <div className={classes.content}>
        <DoneColumnSection workflowId={workflowId} />
        <LaunchConfig workflowId={workflowId} />
        <TemplateCrmConfig workflowId={workflowId} />
        <SkipTriageSection workflowId={workflowId} />
        <WorkflowForwardingSection workflowId={workflowId} />
      </div>
    </div>
  );
});

function SkipTriageSection({ workflowId }: { workflowId: UUID }) {
  const { data } = Apollo.useGetWorkflowWithBoardQuery({ variables: { workflowId } });
  const [updateWorkflow] = Apollo.useUpdateWorkflowMutation();
  const workflow = data?.workflow;
  const boardHasTriage = Boolean(workflow?.column.board.triageColumn?.id);

  if (!workflow || workflow.variant !== Apollo.WorkflowVariant.Template || !boardHasTriage)
    return null;

  return (
    <WorkflowToggleSection
      icon={ListChecks}
      label="Skip Triage on launch"
      title="Skip Triage"
      toggled={workflow.skipTriage}
      onToggle={async () => {
        await updateWorkflow({
          variables: { input: { props: { skipTriage: !workflow.skipTriage }, workflowId } },
        });
      }}
    />
  );
}

function WorkflowToggleSection({
  icon: Icon,
  label,
  onToggle,
  title,
  toggled,
}: {
  icon: LucideIcon;
  label: string;
  onToggle: () => Promise<void>;
  title: string;
  toggled: boolean;
}) {
  const { classes } = useStyles();

  return (
    <SettingsSection>
      <div className={classes.sectionLabel}>
        <Icon className={classes.sectionIcon} size={16} />
        <Typography className={classes.sectionTitle}>{title}</Typography>
      </div>
      <div className={classes.toggleRow}>
        <ToggleSwitch toggle={onToggle} toggled={toggled} />
        <Typography className={classes.toggleText}>{label}</Typography>
      </div>
    </SettingsSection>
  );
}

function WorkflowForwardingSection({ workflowId }: { workflowId: UUID }) {
  const { classes } = useStyles();

  return (
    <SettingsSection>
      <div className={classes.sectionLabel}>
        <Forward className={classes.sectionIcon} size={16} />
        <Typography className={classes.sectionTitle}>Workflow forwarding</Typography>
      </div>
      <WorkflowForwardingToggle workflowId={workflowId} />
    </SettingsSection>
  );
}

function DoneColumnSection({ workflowId }: { workflowId: UUID }) {
  const { classes } = useStyles();
  const { getPickerChipClassName, pickerChipIconClassName, pickerChipLabelClassName } =
    usePickerChipStyles();
  const [updateWorkflow] = Apollo.useUpdateWorkflowMutation();
  const { data } = Apollo.useGetWorkflowWithBoardQuery({ variables: { workflowId } });
  const workflow = data?.workflow;
  const boardId = workflow?.column.board.id;
  const doneColumns = useMemo(
    () => getDoneColumns(workflow?.column.board.columns ?? []),
    [workflow?.column.board.columns]
  );
  const doneColumn = workflow?.doneColumn;
  const doneColumnPosition = useMemo(() => {
    if (!doneColumn || !doneColumns.length) return { index: 1, total: 1 };
    return getColumnPositionInCategory(doneColumn, doneColumns);
  }, [doneColumn, doneColumns]);

  if (!workflow || !doneColumn) return null;

  return (
    <SettingsSection>
      <div className={classes.sectionLabel}>
        <ArrowRightToLine className={classes.sectionIcon} size={16} />
        <Typography className={classes.sectionTitle}>Done column</Typography>
      </div>
      <Typography className={classes.sectionDescription} variant="body2">
        Move the workflow to this column once every phase is complete.
      </Typography>
      <div className={classes.pickerRow}>
        <ColumnPickerPopover
          addStatusOption={boardId ? { boardId, label: 'Add Done status' } : undefined}
          columns={doneColumns}
          renderTrigger={(_, isOpen) => (
            <div className={getPickerChipClassName(isOpen)}>
              <ProgressiveColumnIcon
                boxSize={PICKER_CHIP_ICON_SIZE}
                category={doneColumn.category}
                className={pickerChipIconClassName}
                color={doneColumn.color}
                iconSize={PICKER_CHIP_ICON_SIZE}
                index={doneColumnPosition.index}
                showBackground={false}
                showBorder={false}
                total={doneColumnPosition.total}
              />
              <Typography noWrap className={pickerChipLabelClassName} variant="body2">
                {doneColumn.name}
              </Typography>
            </div>
          )}
          selectedColumnId={doneColumn.id}
          onColumnChange={async doneColumnId => {
            await updateWorkflow({
              variables: {
                input: { props: { doneColumnId }, workflowId },
              },
            });
          }}
        />
      </div>
    </SettingsSection>
  );
}
